import React, { useEffect, useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { handleUnifiedLogin } from 'src/services/UnifiedLoginService';
import { Card, CardBody, Button } from '@nextui-org/react';
import { Alert } from 'antd';
import { useTranslation } from 'react-i18next';

const LoginClub: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleLogin = useCallback(async (code: string) => {
    try {
      setIsLoading(true);
      setError(null);
      
      await handleUnifiedLogin(code, 'club');
      navigate('/Shared');
      
    } catch (error: any) {
      console.error('An error occurred during club login:', error);
      setError(error.message || 'Login failed');
    } finally {
      setIsLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const code = searchParams.get('code');

    if (code) {
      handleLogin(code);
    } else {
      navigate('/500');
    }
  }, [handleLogin, navigate]);

  const handleRetry = () => {
    const searchParams = new URLSearchParams(window.location.search);
    const code = searchParams.get('code');
    if (code) {
      handleLogin(code);
    }
  };

  const handleGoHome = () => {
    navigate('/homepage');
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
        <Card className="w-full max-w-md mx-4">
          <CardBody className="text-center py-12">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary mx-auto mb-6"></div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">
              {t('club.login.authenticating')}
            </h2>
            <p className="text-gray-600">
              {t('club.login.processing')}
            </p>
            <div className="mt-4">
              <div className="flex space-x-2 justify-center">
                <div className="w-2 h-2 bg-primary rounded-full animate-bounce"></div>
                <div className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                <div className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
              </div>
            </div>
          </CardBody>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-red-50 to-pink-100">
        <Card className="w-full max-w-md mx-4">
          <CardBody className="text-center py-8">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-4">
              {t('club.login.error.title')}
            </h2>
            <Alert 
              message={error || 'Login failed'}
              type="error"
              showIcon
              className="mb-6"
            />
            <div className="flex flex-col sm:flex-row gap-3">
              <Button 
                color="primary" 
                variant="flat" 
                onPress={handleRetry}
                className="flex-1"
              >
                {t('club.login.retry')}
              </Button>
              <Button 
                color="default" 
                variant="bordered" 
                onPress={handleGoHome}
                className="flex-1"
              >
                {t('club.login.go.home')}
              </Button>
            </div>
          </CardBody>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <Card className="w-full max-w-md mx-4">
        <CardBody className="text-center py-8">
          <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">
            {t('club.login.success.title')}
          </h2>
          <p className="text-gray-600 mb-6">
            {t('club.login.success.message')}
          </p>
          <Button 
            color="primary" 
            onPress={() => navigate('/homepage')}
            className="w-full"
          >
            {t('club.login.continue')}
          </Button>
        </CardBody>
      </Card>
    </div>
  );
};

export default LoginClub;
    